import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  posSessionsControllerAddHelper,
  posSessionsControllerClose,
  posSessionsControllerFindAll,
  PosSessionsControllerFindAll$Params,
  posSessionsControllerGetApprovers,
  posSessionsControllerGetById,
  posSessionsControllerGetCurrentSession,
  posSessionsControllerGetDetailedOverallStats,
  posSessionsControllerGetDetailedStats,
  posSessionsControllerGetHelpers,
  posSessionsControllerGetOverallStats,
  posSessionsControllerGetStats,
  posSessionsControllerOpen,
  posSessionsControllerRemoveHelper,
  posSessionsControllerSearchAvailableHelpers,
  PosSessionsControllerSearchAvailableHelpers$Params,
  posSessionsControllerUpdateSessionCash,
} from '@klubovna/api-client';
import {
  AddHelperDto,
  AvailableHelperDto,
  ClosePosSessionDto,
  CloseSessionResponseDto,
  DetailedOverallStatsDto,
  DetailedSessionStatsDto,
  HelperResponseDto,
  OpenPosSessionDto,
  PosOverallStatsDto,
  PosSession,
  PosSessionApproverDto,
  PosSessionStatsDto,
  UpdateSessionCashDto,
} from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PosSessionsApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: PosSessionsControllerFindAll$Params): Observable<PaginatedResponse<PosSession>> {
    return posSessionsControllerFindAll(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toPaginatedResponse<PosSession>(response.body)));
  }

  getCurrentSession(): Observable<PosSession | null> {
    return posSessionsControllerGetCurrentSession(this.http, this.apiConfig.rootUrl)
      .pipe(map(response => response.body as PosSession | null));
  }

  open(body: OpenPosSessionDto): Observable<PosSession> {
    return posSessionsControllerOpen(this.http, this.apiConfig.rootUrl, { body })
      .pipe(map(response => response.body as PosSession));
  }

  getApprovers(): Observable<PosSessionApproverDto[]> {
    return posSessionsControllerGetApprovers(this.http, this.apiConfig.rootUrl)
      .pipe(map(response => response.body ?? []));
  }

  close(id: string, body?: ClosePosSessionDto): Observable<CloseSessionResponseDto> {
    return posSessionsControllerClose(this.http, this.apiConfig.rootUrl, { id, body: body || {} })
      .pipe(map(response => response.body!));
  }

  getById(id: string): Observable<PosSession> {
    return posSessionsControllerGetById(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as PosSession));
  }

  getStats(id: string): Observable<PosSessionStatsDto> {
    return posSessionsControllerGetStats(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as PosSessionStatsDto));
  }

  getOverallStats(params?: { dateFrom?: string; dateTo?: string }): Observable<PosOverallStatsDto> {
    return posSessionsControllerGetOverallStats(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as PosOverallStatsDto));
  }

  getDetailedStats(id: string): Observable<DetailedSessionStatsDto> {
    return posSessionsControllerGetDetailedStats(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as DetailedSessionStatsDto));
  }

  getDetailedOverallStats(params?: { dateFrom?: string; dateTo?: string }): Observable<DetailedOverallStatsDto> {
    return posSessionsControllerGetDetailedOverallStats(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as DetailedOverallStatsDto));
  }

  getHelpers(id: string): Observable<HelperResponseDto[]> {
    return posSessionsControllerGetHelpers(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body ?? []));
  }

  addHelper(id: string, body: AddHelperDto): Observable<PosSession> {
    return posSessionsControllerAddHelper(this.http, this.apiConfig.rootUrl, { id, body })
      .pipe(map(response => response.body as PosSession));
  }

  removeHelper(id: string, volunteerId: string): Observable<PosSession> {
    return posSessionsControllerRemoveHelper(this.http, this.apiConfig.rootUrl, { id, volunteerId })
      .pipe(map(response => response.body as PosSession));
  }

  searchAvailableHelpers(params?: PosSessionsControllerSearchAvailableHelpers$Params): Observable<AvailableHelperDto[]> {
    return posSessionsControllerSearchAvailableHelpers(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => {
        const body = response.body;
        if (Array.isArray(body)) return body;
        return (body as any)?.items ?? [];
      }));
  }

  updateSessionCash(id: string, body: UpdateSessionCashDto): Observable<PosSession> {
    return posSessionsControllerUpdateSessionCash(this.http, this.apiConfig.rootUrl, {
      id,
      body,
    }).pipe(map(response => response.body as PosSession));
  }
}
